/**
 * Rabattcodes im Warenkorb umsortieren.
 *
 * Shopify bewertet nur die ersten 5 Rabattcodes, die am Warenkorb haengen, und zwar in
 * gespeicherter Reihenfolge. Wirkungslose Codes belegen dabei Plaetze: Haengen vorne
 * 5 nur gesammelte Codes, wird ein gueltiger 6. Code komplett ignoriert. Der Checkout
 * bewertet alles neu, der Warenkorb zeigt bis dahin eine zu niedrige Ersparnis.
 *
 * Deshalb sortieren wir gueltige Codes nach vorne. Geschrieben wird ueber den
 * dokumentierten discount-Parameter von /cart/update.js, der immer die komplette
 * Liste ersetzt (Ajax Cart API, seit Mai 2025).
 *
 * Laeuft nur, wenn mehr als 5 Codes am Warenkorb haengen, und nur dort, wo der
 * Warenkorb sichtbar wird: Warenkorbseite, Oeffnen des Drawers, Warenkorbaenderung.
 * Abschaltbar ueber die Theme-Einstellung "Fix discount code order".
 */
(() => {
  const MAX_EVALUATED = 5;
  const MAX_ROUNDS = 3;
  const DEBOUNCE = 300;
  const SIGNATURE_KEY = 'discount-reorder:signature';

  const themeVariables = window.themeVariables || {};
  const routes = themeVariables.routes || {};
  const cartUrl = routes.cartUrl || '/cart';
  const cartUpdateUrl = routes.cartUpdateUrl || '/cart/update';
  const isCartPage = (themeVariables.settings || {}).pageType === 'cart';

  const codesOf = (cart) => (cart.discount_codes || []).map((discount) => discount.code);

  // Was der Kunde sieht. Nur wenn sich das aendert, muss die Oberflaeche neu gezeichnet werden.
  const effectOf = (cart) => JSON.stringify([
    cart.total_discount,
    (cart.discount_codes || []).filter((discount) => discount.applicable).map((discount) => discount.code).sort()
  ]);

  // Die Reihenfolge gehoert bewusst nicht dazu: Nach einem Lauf ist der Warenkorb
  // derselbe, nur anders sortiert, und wir wollen nicht endlos erneut sortieren.
  const signatureOf = (cart) => JSON.stringify([
    cart.currency,
    (cart.items || []).map((item) => [item.key, item.quantity]),
    codesOf(cart).slice().sort()
  ]);

  const readSignature = () => {
    try {
      return sessionStorage.getItem(SIGNATURE_KEY);
    } catch (error) {
      return null;
    }
  };

  const writeSignature = (signature) => {
    try {
      sessionStorage.setItem(SIGNATURE_KEY, signature);
      return true;
    } catch (error) {
      return false;
    }
  };

  const fetchCart = async () => {
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });

    if (!response.ok) {
      throw new Error(`cart.js antwortete mit ${response.status}`);
    }

    return response.json();
  };

  const writeCodes = async (codes) => {
    const response = await fetch(`${cartUpdateUrl}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });

    if (!response.ok) {
      throw new Error(`cart/update.js antwortete mit ${response.status}`);
    }

    return response.json();
  };

  // Shopify verraet nur fuer die ersten 5 Plaetze, ob ein Code greift.
  const recordApplicability = (cart, known) => {
    (cart.discount_codes || []).slice(0, MAX_EVALUATED).forEach((discount) => {
      known.set(discount.code, discount.applicable);
    });

    return known;
  };

  async function reorder(initialCart) {
    let cart = initialCart;
    const codes = codesOf(cart);

    if (codes.length <= MAX_EVALUATED) {
      return null;
    }

    const signature = signatureOf(cart);

    if (readSignature() === signature) {
      return null;
    }

    const originalCodes = codes.slice();
    const originalDiscount = cart.total_discount;
    const originalEffect = effectOf(cart);
    const known = recordApplicability(cart, new Map());

    // Ungepruefte Codes nach vorne holen, bis alle einmal auf einem der 5 Plaetze waren.
    for (let round = 0; round < MAX_ROUNDS; round += 1) {
      const applicable = codes.filter((code) => known.get(code) === true);
      const untested = codes.filter((code) => !known.has(code));

      if (untested.length === 0 || applicable.length >= MAX_EVALUATED) {
        break;
      }

      const rejected = codes.filter((code) => known.get(code) === false);
      cart = await writeCodes([...applicable, ...untested, ...rejected]);
      recordApplicability(cart, known);
    }

    const applicable = codes.filter((code) => known.get(code) === true);
    const rest = codes.filter((code) => known.get(code) !== true);
    const finalOrder = [...applicable, ...rest];

    if (finalOrder.join(',') !== codesOf(cart).join(',')) {
      cart = await writeCodes(finalOrder);
    }

    // Sicherheitsnetz: Rabatte koennen sich gegenseitig ausschliessen, eine andere
    // Reihenfolge darf den Kunden nie schlechter stellen.
    if (cart.total_discount < originalDiscount) {
      cart = await writeCodes(originalCodes);
      writeSignature(signatureOf(cart));
      return null;
    }

    // Bewusst der Zustand nach dem Sortieren: Ein Buy-X-Get-Y-Rabatt legt beim Greifen
    // einen Gratisartikel in den Warenkorb. Wuerden wir die Signatur von vorher merken,
    // wuerde sie nie wieder passen und wir wuerden bei jedem Anlass neu sortieren.
    const stored = writeSignature(signatureOf(cart));

    if (effectOf(cart) === originalEffect) {
      return null;
    }

    return { cart, stored };
  }

  function refreshUI(result) {
    // Ohne sections im Detail holt sich der Drawer seinen Abschnitt selbst.
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart: result.cart }
    }));

    // Die Warenkorbseite hat kein Element, das auf cart:refresh neu zeichnet. Wir machen
    // es wie das Bundle-Handling in theme.js und laden neu. Nur wenn die Signatur
    // gespeichert werden konnte, sonst droht eine Endlosschleife aus Neuladen und Sortieren.
    if (isCartPage && result.stored) {
      window.location.reload();
    }
  }

  let running = false;
  let queued = false;
  let timer = null;

  async function run(knownCart) {
    if (running) {
      queued = true;
      return;
    }

    running = true;

    try {
      const cart = knownCart || (await fetchCart());
      const result = await reorder(cart);

      if (result) {
        refreshUI(result);
      }
    } catch (error) {
      console.error('[discount-reorder]', error);
    } finally {
      running = false;

      if (queued) {
        queued = false;
        schedule();
      }
    }
  }

  function schedule(cart) {
    clearTimeout(timer);
    timer = setTimeout(() => run(cart), DEBOUNCE);
  }

  if (isCartPage) {
    schedule();
  }

  document.addEventListener('openable-element:open', (event) => {
    if (event.target && event.target.id === 'mini-cart') {
      schedule();
    }
  }, true);

  // Das Theme haengt den kompletten Warenkorb ans Event, das spart eine Abfrage.
  document.documentElement.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    schedule(cart && cart.discount_codes ? cart : undefined);
  });
})();
